import { Field, IconButton, Modal, Text, TextInput } from "@carbon6/c6-frontend-design";
import { useState, type ChangeEvent, type ReactNode, type SyntheticEvent } from "react";
import { canDeleteShared, canModifyEmbodiment, type MePermissions, type UserRole } from "@/app";
import { hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import { PartImagePlaceholder } from "@/components/embodiment";
import {
  emptyPartFormValues,
  partRecordToFormValues,
  type PartFormValues,
  type PartKindMeta,
} from "../partsCatalog";
import type { PartDialogState } from "../EmbodimentPageTypes";
import styles from "../EmbodimentPage.module.css";

type PartFormDialogProps = {
  dialog: Extract<PartDialogState, { kind: "form" }>;
  meta: PartKindMeta;
  role: UserRole | null;
  // 서버가 산출한 삭제 권한. 전달하지 않으면 등급으로 폴백한다. 삭제 버튼 표시에 쓴다.
  permissions?: MePermissions | null;
  onClose: () => void;
  onSubmit: (values: PartFormValues) => void;
  onDelete: () => void;
};

export function PartFormDialog({
  dialog,
  meta,
  role,
  permissions,
  onClose,
  onSubmit,
  onDelete,
}: PartFormDialogProps): ReactNode {
  const canModify = canModifyEmbodiment(role);
  // 삭제는 공유 카탈로그 권한(서버 SoT)으로 판정한다. 전달하지 않으면 등급(admin)으로 폴백한다.
  const canDelete = canDeleteShared(permissions ?? null, role);
  const [values, setValues] = useState<PartFormValues>(() =>
    dialog.part ? partRecordToFormValues(dialog.part) : emptyPartFormValues(),
  );
  const titleText =
    dialog.mode === "create"
      ? `새 ${meta.singular} 항목을 만듭니다.`
      : canModify
        ? `${meta.singular} 항목을 편집합니다.`
        : `${meta.singular} 항목을 확인합니다.`;

  function patch(next: Partial<PartFormValues>): void {
    setValues((prev) => ({ ...prev, ...next }));
  }

  function handleSubmit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void {
    event.preventDefault();
    if (!canModify || dialog.submitting) {
      return;
    }
    onSubmit(values);
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      closeOnBackdropClick={!dialog.submitting}
      closeOnEscape={!dialog.submitting}
      aria-labelledby="part-form-dialog-title"
    >
      {/* 삭제는 편집 모드이고 권한이 있을 때만 우상단에 표시한다(없으면 HatModalHeader 가 숨긴다). 저장·취소는 footer 에 둔다. */}
      <HatModalHeader
        title={titleText}
        titleId="part-form-dialog-title"
        delete={
          dialog.mode === "edit" && canDelete
            ? { onClick: onDelete, disabled: dialog.submitting }
            : undefined
        }
        close={{ disabled: dialog.submitting }}
      />
      <form className={styles.dialogForm} onSubmit={handleSubmit}>
        <PartImagePlaceholder />
        <Text variant={hatcheryTextModes["caption-bold"]} className={styles.dialogEyebrow}>
          {meta.singular}
        </Text>

        <div className={styles.fields}>
          <Field label="이름 *" layout="stack">
            <TextInput
              value={values.name}
              disabled={!canModify || dialog.submitting}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                patch({ name: event.target.value })
              }
            />
          </Field>
          {meta.hasTag ? (
            <Field label="태그" layout="stack">
              <TextInput
                value={values.tag}
                disabled={!canModify || dialog.submitting}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  patch({ tag: event.target.value })
                }
              />
            </Field>
          ) : null}
          <Field label="설명" layout="stack">
            <TextInput
              value={values.description}
              disabled={!canModify || dialog.submitting}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                patch({ description: event.target.value })
              }
            />
          </Field>
        </div>

        {dialog.errorMessage ? (
          <Text
            variant={hatcheryTextModes["body-normal"]}
            className={styles.dialogError}
            role="alert"
          >
            {dialog.errorMessage}
          </Text>
        ) : null}

        <div className={styles.dialogActions}>
          <IconButton
            text={canModify ? "취소" : "닫기"}
            type="button"
            variant="surface"
            disabled={dialog.submitting}
            onClick={onClose}
          />
          {canModify ? (
            <IconButton
              text={dialog.submitting ? "저장 중" : "저장"}
              type="submit"
              variant="solid"
              tone="primary"
              disabled={dialog.submitting || values.name.trim() === ""}
            />
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
